import { Component, Input, OnChanges, SimpleChanges } from '@angular/core'
import { MessageService } from 'primeng/api'

import { ApiClientBankAccountsService, ClientBankAccount } from '../../data/services/api/api-client-bank-accounts.service'

/** Una CLABE son 18 dígitos. Ni 17 ni 19. La misma regla que valida el backend. */
const CLABE = /^\d{18}$/

/**
 * Las cuentas bancarias **desde las que paga** este cliente.
 *
 * ## Para qué sirve capturarlas
 *
 * Es lo que permite reconocer un depósito **sin referencia**: la CLABE ordenante del SPEI identifica
 * al cliente sola, sin depender de que alguien haya escrito bien un concepto. Es la regla más fuerte
 * de la conciliación bancaria y **la única que no se puede equivocar**, porque una cuenta pertenece a
 * un cliente y a nadie más.
 *
 *! Hasta ahora este dato no existía en el sistema. Mientras el catálogo esté vacío, la conciliación
 *! cae a «cliente + monto», que casi nunca desempata, y casi todo sale «por identificar».
 *
 * ## Lo que NO es
 *
 * No son las cuentas de Rendafin —Santander, Covalto, Bajío—, que son el **destino** y viven en otro
 * lado. Aquí se guarda el **origen**.
 */
@Component({
  selector: 'app-customer-bank-accounts',
  templateUrl: './customer-bank-accounts.component.html',
  styleUrls: ['./customer-bank-accounts.component.scss'],
})
export class CustomerBankAccountsComponent implements OnChanges {
  @Input() client: any

  cuentas: ClientBankAccount[] = []
  cargando = false
  error = ''

  /** Por qué el servidor rechazó el alta, dicho junto al campo y no solo en un toast que se va. */
  errorAlta = ''

  // ── El alta ─────────────────────────────────────────────────────────────────────────────────
  mostrarAlta = false
  guardando = false
  clabe = ''
  banco = ''
  titular = ''

  constructor(
    private readonly api: ApiClientBankAccountsService,
    private readonly toast: MessageService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['client'] && this.clientId) this.cargar()
  }

  get clientId(): string {
    return this.client?._id || this.client?.id || ''
  }

  /** Las activas primero: una cuenta dada de baja es historia, no operación. */
  get activas(): ClientBankAccount[] {
    return this.cuentas.filter((c) => c.active)
  }

  get inactivas(): ClientBankAccount[] {
    return this.cuentas.filter((c) => !c.active)
  }

  cargar(): void {
    if (!this.clientId) return
    this.cargando = true
    this.error = ''
    this.api.findByClient(this.clientId).subscribe((r) => {
      this.cargando = false
      if (r.error) {
        this.error = r.msg
        return
      }
      this.cuentas = r.data || []
    })
  }

  abrirAlta(): void {
    this.clabe = ''
    this.banco = ''
    this.titular = ''
    this.errorAlta = ''
    this.mostrarAlta = true
  }

  /** La CLABE se limpia al vuelo: los estados de cuenta las escriben con espacios y guiones. */
  get clabeLimpia(): string {
    return (this.clabe || '').replace(/[\s-]/g, '')
  }

  get clabeValida(): boolean {
    return CLABE.test(this.clabeLimpia)
  }

  guardar(): void {
    if (!this.clabeValida || !this.clientId) return
    this.guardando = true
    this.errorAlta = ''
    this.api.create({ clabe: this.clabeLimpia, client: this.clientId, bank: this.banco.trim() || null, holderName: this.titular.trim() || null }).subscribe((r) => {
      this.guardando = false
      if (r.error) {
        //* El conflicto más importante —«ya es de otro cliente»— viene del servidor con su texto.
        //* No se reinterpreta aquí: reasignar una CLABE cambiaría de dueño los depósitos viejos.
        //* Se deja junto al campo además del toast: el diálogo sigue abierto y el toast se va.
        this.errorAlta = r.msg
        this.toast.add({ severity: 'error', summary: 'No se registró', detail: r.msg, life: 6000 })
        return
      }
      this.mostrarAlta = false
      this.toast.add({ severity: 'success', summary: 'Cuenta registrada', detail: 'Los depósitos que lleguen desde esa CLABE se van a reconocer solos' })
      this.cargar()
    })
  }

  desactivar(cuenta: ClientBankAccount): void {
    this.api.deactivate(cuenta._id).subscribe((r) => {
      if (r.error) return this.toast.add({ severity: 'error', summary: 'No se desactivó', detail: r.msg })
      this.toast.add({ severity: 'success', summary: 'Cuenta desactivada', detail: 'Se conserva para explicar los movimientos viejos' })
      this.cargar()
    })
  }

  /** De dónde salió la cuenta, en palabras. */
  origen(c: ClientBankAccount): string {
    return { MANUAL: 'capturada', IMPORT: 'carga masiva', OBSERVED: 'deducida de un depósito' }[c.source] || c.source
  }

  /** La CLABE en grupos, para poder leerla y cotejarla contra un estado de cuenta. */
  formatear(clabe: string): string {
    return (clabe || '').replace(/(\d{3})(\d{3})(\d{11})(\d{1})/, '$1 $2 $3 $4')
  }
}
